import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import 'react-phone-number-input/style.css';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
// @types
import { User } from '../../../@types/user';
// _mock
import { countries } from '../../../_mock';
// components
import Iconify from '../../../components/Iconify';
import TextMaxLine from '../../../components/TextMaxLine';
import { CustomFile, UploadAvatar } from '../../../components/upload';
import { FormProvider, RHFSelect, RHFTextField, RHFSwitch } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { Package } from 'src/@types/package';
import { APIS } from 'src/utils/serviceUrls';
import { fDate } from 'src/utils/formatTime';
import { cairoEndLabel, cairoToday, untilEndOfCairoDay } from 'src/utils/cairoTime';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// ----------------------------------------------------------------------

interface FormValuesProps extends Omit<User, 'avatarUrl'> {
  avatarUrl: CustomFile | string | null;
  // Legacy single diploma id (superseded by `diplomas`).
  diploma?: string;
  // Multiple diploma enrolments (diploma + batch). A student can be in 2+.
  diplomas?: { diploma: string; batchName: string }[];
  // Individually assigned courses (ids). Opens those courses for the student
  // regardless of package/track/diploma; the API also auto-enrols them.
  assignedCourses?: string[];
  // Individually assigned tracks (ids). Opens every course inside those tracks
  // for the student regardless of package/diploma; the API also auto-enrols them.
  assignedTracks?: string[];
  // "Access ends" — the last day of access, Cairo time (yyyy-mm-dd, '' = no
  // end date). Edit form only; sent to the segment endpoint when changed as the
  // bare day, which the API stores as the end of that day in Cairo (owner Q2(c)).
  accessEndsOn?: string;
  // Free access with no end — must be chosen explicitly (FEATURES D1.5).
  freeAccessLifetime?: boolean;
}

// Stored subscriptionEndDate (an exclusive end instant) -> the last Cairo day
// it covers: an end at 00:00 Cairo covers the day before (the API stores
// "until 29 Oct" as 30 Oct 00:00 Cairo), an end at 11:00 covers that day.
const toDateInput = (value?: string | Date | null): string => {
  if (!value) return '';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '';
  return cairoToday(new Date(t - 1));
};

// Grants a student paid for (Paymob or a 100% promo): only a refund or the
// Access panel ends them (owner Q5, FEATURES D1.6).
const PAID_SOURCES = ['payment', 'promo'];

// The API's rule (users.repository isFreePackage): named Free, or costs nothing.
const isFreePackage = (pkg?: any): boolean =>
  !!pkg &&
  (String(pkg.name ?? '').trim().toLowerCase() === 'free' ||
    (Number.isFinite(Number(pkg.cost)) && Number(pkg.cost) <= 0) ||
    (pkg.price !== undefined && Number.isFinite(Number(pkg.price)) && Number(pkg.price) <= 0));

// Readable text from an API error (the axios instance rejects with the body:
// `{ message }` or a validation `{ messages: [...] }`).
const apiErrorText = (err: any): string => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (Array.isArray(err.messages) && err.messages.length) {
    return err.messages
      .map((m: any) =>
        typeof m === 'string'
          ? m
          : m?.constraints
          ? Object.values(m.constraints).join(', ')
          : m?.message || JSON.stringify(m)
      )
      .join('; ');
  }
  if (Array.isArray(err.message)) return err.message.join('; ');
  return String(err.message || 'Unknown error');
};

/**
 * POST /users/create phone refusals (FEATURES §A4) → the Phone field's error, or
 * null. `phone_unactivated_in_use` carries the holder's masked email and
 * creation date in `detail` (staff only; the public sign-up never sees them).
 */
function createPhoneError(err: any): string | null {
  if (err?.code === 'phone_unactivated_in_use') {
    const d = err?.detail || {};
    const who = [d.holderEmailMasked, d.holderCreatedAt ? `created ${fDate(d.holderCreatedAt)}` : '']
      .filter(Boolean)
      .join(', ');
    return (
      'This number belongs to an account that was never activated but already holds access or payments' +
      `${who ? ` (${who})` : ''}. Ask the student to activate it from its email or sign in with it, or use another number.`
    );
  }
  if (err?.code === 'phone_exists' || String(err?.message || '').includes('phone_1 dup key')) {
    return 'A student with this phone number already exists.';
  }
  return null;
}

/**
 * react-phone-input-2 hands back bare digits ("201001234567"), while accounts
 * store the number in E.164 ("+201001234567"). Posting the digits unchanged
 * made the phone uniqueness check miss the existing student (exact match), so
 * a second account could take the same number. Add the "+" before any save.
 */
function withLeadingPlus(phone?: string | null): string | null | undefined {
  if (!phone) return phone;
  const p = String(phone).trim();
  return p.startsWith('+') ? p : `+${p}`;
}

type Props = {
  isEdit: boolean;
  currentUser?: any;
  handleClose(saved?: boolean): void;
};

export default function UserNewEditForm({ isEdit, currentUser, handleClose }: Props) {
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const [country, setCountry] = useState<any>();
  const [packages, setPackages] = useState<Package[]>([]);
  // Active diplomas (with their batches) for the diploma + batch dropdowns.
  const [diplomas, setDiplomas] = useState<any[]>([]);
  // Enrolled count per batch, keyed `${diplomaId}|${batchName}` (full-batch warning).
  const [batchCounts, setBatchCounts] = useState<Record<string, number>>({});
  // Real promo codes for the dropdown (so the entered code always resolves).
  const [promoCodes, setPromoCodes] = useState<any[]>([]);
  // All courses, for the "Assign courses" picker (grant individual courses).
  const [courses, setCourses] = useState<any[]>([]);
  // The student's PAID grants (edit only): the form warns before a change the
  // API refuses (Free package / removing a paid bootcamp, FEATURES D1.6).
  const [paidGrants, setPaidGrants] = useState<any[]>([]);
  // All tracks, for the "Assign tracks" picker (grant whole tracks).
  const [tracks, setTracks] = useState<any[]>([]);
  const NewUserSchema = Yup.object()
    .shape({
      name: Yup.string().required('Name is required'),
      email: Yup.string().email().required('Email is required'),
      phone: Yup.string().required('Phone number is required'),
      country: Yup.string().required('country is required'),
      ...(isEdit ? {} : { password: Yup.string().required('Company is required').min(8) }),
      // Required only when creating. On edit a student may have no package (or
      // a bare id the list did not populate); that must not block saving other
      // fields, and the package is only sent when the admin changes it.
      package: isEdit ? Yup.string() : Yup.string().required('package is required'),
      avatar: Yup.string(), // optional
      batchName: Yup.string(),
      diploma: Yup.string(),
      diplomas: Yup.array(),
      assignedCourses: Yup.array(),
      assignedTracks: Yup.array(),
      promoCode: Yup.string(),
      freeAccess: Yup.boolean(),
      freeAccessNote: Yup.string(),
      accessEndsOn: Yup.string(),
      freeAccessLifetime: Yup.boolean(),
    })
    .transform((obj, originalObj) => {
      obj.firstName = originalObj.name.split(' ')[0];
      obj.lastName = originalObj.name.split(' ')[1];
      return obj;
    });

  const defaultValues = useMemo(
    () => ({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
      country: currentUser?.country || '',
      password: currentUser?.password || '',
      // The list returns `package` unpopulated (a bare id); other callers may
      // pass the populated object. Accept both.
      package:
        (typeof currentUser?.package === 'object'
          ? currentUser?.package?._id
          : currentUser?.package) || '',
      avatar: currentUser?.avatar || '',
      firstName: currentUser?.firstName || '',
      batchName: currentUser?.batchName || '',
      // Seed the enrolment editor from the new array, or fall back to the
      // legacy single diploma so older records open with one row.
      diplomas: (() => {
        const arr = (currentUser as any)?.diplomas;
        if (Array.isArray(arr) && arr.length) {
          return arr.map((d: any) => ({
            diploma: (typeof d.diploma === 'object' ? d.diploma?._id : d.diploma) || '',
            batchName: d.batchName || '',
          }));
        }
        const legacy = (currentUser as any)?.diploma;
        if (legacy) {
          return [
            {
              diploma: (typeof legacy === 'object' ? legacy?._id : legacy) || '',
              batchName: currentUser?.batchName || '',
            },
          ];
        }
        return [];
      })(),
      // Individually assigned courses. The API may return ids or populated
      // course objects, so normalise to ids for the picker.
      assignedCourses: (((currentUser as any)?.assignedCourses ?? []) as any[])
        .map((c: any) => (typeof c === 'object' ? c?._id : c))
        .filter(Boolean),
      // Individually assigned tracks. The API may return ids or populated track
      // objects, so normalise to ids for the picker.
      assignedTracks: (((currentUser as any)?.assignedTracks ?? []) as any[])
        .map((t: any) => (typeof t === 'object' ? t?._id : t))
        .filter(Boolean),
      promoCode: currentUser?.promoCode || '',
      freeAccess: currentUser?.freeAccess || false,
      freeAccessNote: currentUser?.freeAccessNote || '',
      accessEndsOn: toDateInput((currentUser as any)?.subscriptionEndDate),
      freeAccessLifetime: false,
    }),
    [currentUser]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    getFieldState,
    setValue,
    setError,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  // Free access being turned on now (FEATURES D1.5): a reason and an end date
  // or an explicit Lifetime are required, like the Access panel.
  const compTurningOn = isEdit && !!values.freeAccess && !defaultValues.freeAccess;
  const compLifetime = compTurningOn && !!values.freeAccessLifetime;

  // Helper line under "Access ends": the exact instant, date + time + Cairo time.
  const storedEnd = (currentUser as any)?.subscriptionEndDate;
  const accessEndsUnchanged = (values.accessEndsOn || '') === (defaultValues.accessEndsOn || '');
  const accessEndsAt = accessEndsUnchanged && storedEnd ? new Date(storedEnd) : untilEndOfCairoDay(values.accessEndsOn || '');
  const accessEndsHelper = compLifetime
    ? 'Lifetime: the free access has no end.'
    : !accessEndsAt
    ? compTurningOn
      ? 'Required: the last day of the free access (or switch on Lifetime).'
      : 'No end date set.'
    : accessEndsAt.getTime() <= Date.now()
    ? `Ended ${cairoEndLabel(accessEndsAt)}.`
    : `Access ends ${cairoEndLabel(accessEndsAt)}.`;

  // What the API would refuse (paid access stays; owner Q5).
  const paidUntil = (pred: (g: any) => boolean): Date | null | undefined => {
    const rows = paidGrants.filter(pred);
    if (!rows.length) return undefined;
    if (rows.some((g) => !g.endsAt)) return null;
    return new Date(Math.max(...rows.map((g) => new Date(g.endsAt).getTime())));
  };
  const pickedPackage = packages.find((p) => String(p._id) === String(values.package || ''));
  const freePackageRefused =
    isEdit &&
    String(values.package || '') !== String(defaultValues.package || '') &&
    isFreePackage(pickedPackage)
      ? paidUntil((g) => g.scope === 'all-access')
      : undefined;
  const keptDiplomaIds = new Set(
    ((values.diplomas || []) as any[]).map((d: any) => String(d?.diploma || '')).filter(Boolean)
  );
  const paidRemoved = ((defaultValues.diplomas || []) as any[])
    .map((d: any) => String(d?.diploma || ''))
    .filter((id) => id && !keptDiplomaIds.has(id))
    .map((id) => ({
      id,
      title: diplomas.find((x: any) => String(x._id) === id)?.title || 'bootcamp',
      until: paidUntil((g) => g.scope === 'diploma' && String(g.target) === id),
    }))
    .filter((x) => x.until !== undefined);
  const paidText = (what: string, until: Date | null) =>
    `This student has a paid ${what} ${until ? `until ${cairoEndLabel(until)}` : 'with no end'}. ` +
    'Refund it in Paymob or revoke it in Access with a reason.';

  // Multiple diploma enrolments (a student can be in 2+ live diplomas).
  const {
    fields: diplomaFields,
    append: appendDiploma,
    remove: removeDiploma,
  } = useFieldArray({ control, name: 'diplomas' as never });

  // Active batches of a given diploma id (for that row's batch dropdown).
  const batchesOf = (diplomaId: string): any[] => {
    const d = diplomas.find((x: any) => x._id === diplomaId);
    return (d?.batches || []).filter((b: any) => b.active !== false);
  };
  // Is a (diploma, batch) full? (enrolled >= capacity).
  const isBatchFull = (diplomaId: string, batchName: string): boolean => {
    const b = batchesOf(diplomaId).find((x: any) => x.name === batchName);
    if (!b?.capacity) return false;
    return (batchCounts[`${diplomaId}|${batchName}`] || 0) >= b.capacity;
  };

  useEffect(() => {
    if (isEdit && currentUser) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentUser]);

  const AddNewStudent = async (data: FormValuesProps) => {
    const mobileValid = isValidPhoneNumber(
      values.phone.slice((country as any)?.dialCode.length, values.phone.length),
      {
        defaultCountry: String((country as any)?.countryCode).toLocaleUpperCase() as any,
        defaultCallingCode: (country as any)?.dialCode,
      }
    );
    if (!mobileValid) {
      setError('phone', {
        message: 'Wrong Phone Number',
        type: 'validate',
      });
      return;
    }

    // Photo is optional: don't send an empty avatar (the API rejects "").
    if (!data.avatar) delete (data as any).avatar;
    // The phone input gives digits without "+": store E.164.
    (data as any).phone = withLeadingPlus((data as any).phone);
    // Multi-diploma enrolments: keep only rows with a chosen diploma.
    (data as any).diplomas = ((data as any).diplomas || [])
      .filter((d: any) => d && d.diploma)
      .map((d: any) => ({ diploma: d.diploma, batchName: d.batchName || '' }));
    // The legacy single field is superseded by `diplomas` — never send it.
    delete (data as any).diploma;
    // "Access ends" is edit-only (a new account gets its window from the package).
    delete (data as any).accessEndsOn;
    // Free access is given on edit, with its reason and end (FEATURES D1.5);
    // the create call never carries it.
    delete (data as any).freeAccess;
    delete (data as any).freeAccessNote;
    delete (data as any).freeAccessLifetime;

    const response = await axiosInstance
      .post(APIS.USERS.ADD, data)
      .then(() => {
        reset();
        enqueueSnackbar('Create success!');
        handleClose(true);
      })
      .catch((err: any) => {
        const phoneError = createPhoneError(err);
        if (phoneError) {
          setError('phone', { type: 'server', message: phoneError });
          enqueueSnackbar('Not created: see the phone number.', { variant: 'error' });
          return;
        }
        if (err?.code === 'email_exists') {
          setError('email', { type: 'server', message: 'email is already in use' });
        }
        if (err?.message) {
          enqueueSnackbar(
            String(err?.message).includes('email_1 dup key')
              ? 'email is already in use'
              : err?.message,
            { variant: 'error' }
          );
        }
        if (err?.messages?.length && err?.messages[0]) {
          console.log('tet1', err);

          enqueueSnackbar(err?.messages.length && err?.messages[0], { variant: 'error' });
        } else {
          enqueueSnackbar(!isEdit ? 'Unable to  Create student!' : 'Unable to  Update student!', {
            variant: 'error',
          });
        }
      });
  };
  const editStudentData = async (data: FormValuesProps) => {
    if (country) {
      const mobileValid = isValidPhoneNumber(
        values.phone.slice((country as any)?.dialCode.length, values.phone.length),
        {
          defaultCountry: String((country as any)?.countryCode).toLocaleUpperCase() as any,
          defaultCallingCode: (country as any)?.dialCode,
        }
      );
      if (!mobileValid) {
        setError('phone', {
          message: 'Wrong Phone Number',
          type: 'validate',
        });
        return;
      }
    }

    // Photo is optional: don't send an empty avatar (the API rejects "").
    if (!data.avatar) delete (data as any).avatar;
    // The phone input gives digits without "+": store E.164.
    (data as any).phone = withLeadingPlus((data as any).phone);

    // "Access ends" is a segment field, never part of the profile PATCH. Send it
    // ONLY when the admin changed it, so an unrelated edit cannot rewrite the
    // stored date (a cleared field sends null = no end date). The bare day is
    // sent: the API stores it as the end of that day, Cairo time.
    const accessEndsOn = (data as any).accessEndsOn || '';
    const turningOn = !!(data as any).freeAccess && !defaultValues.freeAccess;
    const lifetime = turningOn && !!(data as any).freeAccessLifetime;
    delete (data as any).accessEndsOn;
    delete (data as any).freeAccessLifetime;
    const accessEndsChanged = accessEndsOn !== (defaultValues.accessEndsOn || '');

    // Checked here, before anything is saved (the API refuses the same).
    if (turningOn) {
      let bad = false;
      if (String((data as any).freeAccessNote || '').trim().length < 3) {
        setError('freeAccessNote' as any, { type: 'validate', message: 'Give the reason (at least 3 characters).' });
        bad = true;
      }
      const end = untilEndOfCairoDay(accessEndsOn);
      if (!lifetime && (!end || end.getTime() <= Date.now())) {
        setError('accessEndsOn' as any, {
          type: 'validate',
          message: end ? 'This day has passed.' : 'Choose the last day of the free access, or switch on Lifetime.',
        });
        bad = true;
      }
      if (bad) {
        enqueueSnackbar('Nothing was saved: free access needs a reason and an end (or Lifetime).', {
          variant: 'error',
        });
        return;
      }
    }

    // Send `package` ONLY when the admin actually picked a different one.
    // Re-sending the unchanged package on every save let the API recompute
    // isPremium from the package price (F-50), so an unrelated edit (a phone
    // number) could flip premium. `null` rather than omitting the key: the
    // current UpdateUserDto defaults a missing `package` to '' and rejects it
    // as "package must be a mongodb id"; null passes @IsOptional and the API
    // leaves the package untouched.
    const packageChanged = !!data.package && data.package !== (defaultValues.package || '');
    const profilePayload: any = { ...data, package: packageChanged ? data.package : null };

    const id = currentUser?._id || '';
    const segmentPayload = {
      // source is NOT sent — it's set automatically (admin=create,
      // direct=signup) and must not be overwritten on edit.
      freeAccess: (data as any).freeAccess,
      freeAccessNote: (data as any).freeAccessNote,
      batchName: (data as any).batchName,
      // Multi-diploma enrolments (replace the whole set); clear the legacy
      // single field so the array is the single source of truth.
      diplomas: ((data as any).diplomas || [])
        .filter((d: any) => d && d.diploma)
        .map((d: any) => ({ diploma: d.diploma, batchName: d.batchName || '' })),
      diploma: null,
      promoCode: (data as any).promoCode,
      ...(lifetime
        ? { lifetime: true }
        : accessEndsChanged || turningOn
        ? { subscriptionEndDate: accessEndsOn || null }
        : {}),
    };

    // Two requests, each reported on its own (F-51): the old code showed
    // "Update success!" even when the second request had failed.
    // 1) Profile, package and assigned courses/tracks.
    try {
      await axiosInstance.patch(APIS.USERS.BYId(id), profilePayload);
    } catch (err: any) {
      if (err?.code === 'paid_access_remains') {
        setError('package', { type: 'server', message: apiErrorText(err) });
      }
      const phoneError = createPhoneError(err);
      if (phoneError) setError('phone', { type: 'server', message: phoneError });
      enqueueSnackbar(`Nothing was saved. ${apiErrorText(err)}`, {
        variant: 'error',
        persist: true,
      });
      return;
    }

    // 2) Admin-only segment fields: free access, diplomas, promo code, access end date.
    try {
      await axiosInstance.patch(APIS.USERS.SEGMENT(id), segmentPayload);
    } catch (err: any) {
      if (err?.code === 'comp_reason_required') {
        setError('freeAccessNote' as any, { type: 'server', message: apiErrorText(err) });
      }
      if (err?.code === 'comp_end_required') {
        setError('accessEndsOn' as any, { type: 'server', message: apiErrorText(err) });
      }
      // Keep the form open so the admin can correct the field and save again.
      enqueueSnackbar(
        `Profile${packageChanged ? ', package' : ''} and assigned courses/tracks were saved, ` +
          'but free access, diplomas, promo code and access end date were NOT saved. ' +
          apiErrorText(err),
        { variant: 'error', persist: true }
      );
      return;
    }

    reset();
    enqueueSnackbar('Update success! All changes were saved.');
    handleClose(true);
  };
  // Returned so react-hook-form's isSubmitting covers the whole save.
  const onSubmit = async (data: FormValuesProps) =>
    isEdit ? editStudentData(data) : AddNewStudent(data);
  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };
  useEffect(() => {
    (async () => {
      const response = await axiosInstance.get(APIS.PACKAGES.LIST);
      setPackages(response.data.data);
    })().catch(console.error);
  }, []);

  // The student's paid grants, for the warnings above the save (edit only).
  useEffect(() => {
    const uid = currentUser?._id;
    if (!isEdit || !uid) return;
    axiosInstance
      .get(APIS.ENTITLEMENTS.BY_USER(String(uid)))
      .then((res) => {
        const d = res.data?.data ?? res.data;
        const now = Date.now();
        setPaidGrants(
          (Array.isArray(d?.grants) ? d.grants : []).filter(
            (g: any) =>
              !g.revokedAt &&
              PAID_SOURCES.includes(String(g.source)) &&
              (!g.endsAt || new Date(g.endsAt).getTime() > now)
          )
        );
      })
      .catch(() => setPaidGrants([]));
  }, [isEdit, currentUser]);

  // Load active diplomas (with batches) for the diploma + batch dropdowns.
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.DIPLOMAS.ACTIVE);
      const list = res.data?.data ?? res.data;
      setDiplomas(Array.isArray(list) ? list : []);
    })().catch(() => setDiplomas([]));
  }, []);

  // Load all courses for the "Assign courses" picker — from the STAFF list.
  // The public `GET /courses` is Kayfa-only (partner courses live on their own
  // Partner courses tab), so reading it here made partner courses impossible
  // to assign. The staff list includes partner courses and drafts; it pages,
  // so every page is read.
  useEffect(() => {
    (async () => {
      const all: any[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const res = await axiosInstance.get(APIS.COURSES.PAGINATED, {
          params: { page, pageSize: 100 },
        });
        const body = res.data?.data ?? res.data;
        const items = Array.isArray(body?.items) ? body.items : [];
        all.push(...items);
        if (items.length < 100 || all.length >= Number(body?.total ?? 0)) break;
      }
      setCourses(all);
    })().catch(() => setCourses([]));
  }, []);

  // Load all tracks for the "Assign tracks" picker.
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.TRACKS.LIST);
      const d: any = res.data?.data ?? res.data;
      setTracks(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []);
    })().catch(() => setTracks([]));
  }, []);

  // Load enrolled counts per batch (for the full-batch warning; best-effort).
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.DIPLOMAS.ENROLLMENT_COUNTS);
      const list = res.data?.data ?? res.data ?? [];
      const map: Record<string, number> = {};
      (Array.isArray(list) ? list : []).forEach((r: any) => {
        map[`${r.diploma}|${r.batchName}`] = r.count;
      });
      setBatchCounts(map);
    })().catch(() => setBatchCounts({}));
  }, []);

  // Load real promo codes for the dropdown — active + not expired only, so a
  // selected code always resolves to a discount (best-effort; admin-only API).
  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.PROMO_CODE.LIST);
      const list = res.data?.data ?? res.data ?? [];
      const now = Date.now();
      const usable = (Array.isArray(list) ? list : []).filter(
        (p: any) =>
          p?.isActive && (!p?.expiryDate || new Date(p.expiryDate).getTime() > now)
      );
      setPromoCodes(usable);
    })().catch(() => setPromoCodes([]));
  }, []);

  return (
    <>
      <style>
        {`
        .my-container-class:{
          border-radius:8px ;
          height:auto;
          border:1px solid #000
        }
        `}
      </style>
      <Stack>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <TextMaxLine marginBottom={'8px'} fontSize={'22px'}>
            {isEdit ? 'Edit User' : 'Create User'}
          </TextMaxLine>
          <Stack direction={'column'} spacing={3}>
            <Stack alignItems={'center'} justifyContent={'center'}>
              <UploadAvatar
                accept={{ 'image/*': [] }}
                error={getFieldState('avatar').error ? true : false}
                maxFiles={1}
                file={values.avatar ?? null}
                onDropAccepted={async (file) => {
                  let response = await uploadImage(file[0]);
                  setValue('avatar', response?.url);
                }}
              />
              <p
                style={{
                  color: '#FF4842',
                  width: '100%',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  marginTop: '10px',
                }}
              >
                {errors?.avatar && String(errors?.avatar?.message)}
              </p>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="name" label="Full Name" />
              <RHFTextField name="email" type={'email'} label="Email Address" />
              <div>
                <PhoneInput
                  enableAreaCodes={true}
                  placeholder="phone Number"
                  country="ae"
                  inputProps={{
                    name: 'phone',
                    required: true,
                  }}
                  value={values.phone}
                  onChange={(value, country) => {
                    setCountry(country);
                    setValue('phone', value);
                  }}
                  containerStyle={{
                    borderRadius: '8px',
                    height: '57px',
                  }}
                  enableSearch={false}
                  inputStyle={{
                    height: '57px',
                    border: `1px solid ${errors.phone ? '#FF4842' : '#dbe0e4'}`,
                    width: '100%',
                  }}
                />
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.phone && String(errors?.phone?.message)}
                </p>
              </div>

              <RHFSelect name="country" label="Country" placeholder="Country">
                <option value="" />
                {countries.map((option) => (
                  <option key={option.code} value={option.label}>
                    {option.label}
                  </option>
                ))}
              </RHFSelect>
              {!isEdit && <RHFTextField name="password" type={'password'} label="Password" />}
              <RHFSelect name="package">
                <option disabled={true} key={'default'} value={''}>
                  {isEdit ? 'No package (left unchanged)' : 'package'}
                </option>
                {packages.map((option) => (
                  <option key={option._id} value={option._id}>
                    {option.name}
                  </option>
                ))}
              </RHFSelect>
              {freePackageRefused !== undefined && (
                <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
                  {paidText('subscription', freePackageRefused)} The Free package would be refused.
                </Alert>
              )}

              {/* Individually assigned courses — open one or more specific
                  courses for this student, independent of their package /
                  track / diploma. The API auto-enrols each newly added course
                  so it appears in the student's My Courses right away. */}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Autocomplete
                  multiple
                  id="assignedCourses"
                  options={courses}
                  value={courses.filter((c: any) =>
                    (values.assignedCourses ?? []).includes(c?._id)
                  )}
                  getOptionLabel={(option: any) =>
                    `${option?.name || option?.title || option?.slug || ''}${
                      option?.partner ? ' (Partner course)' : ''
                    }${option?.active === false ? ' (Draft)' : ''}`
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?._id === value?._id
                  }
                  onChange={(_e, selected: any[]) =>
                    setValue(
                      'assignedCourses',
                      (selected ?? []).map((c: any) => c?._id).filter(Boolean),
                      { shouldDirty: true }
                    )
                  }
                  renderTags={(selected: any[], getTagProps) =>
                    selected.map((option: any, index: number) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option?._id ?? index}
                        size="small"
                        variant="outlined"
                        label={option?.name || option?.title || ''}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign courses (optional)"
                      placeholder={courses.length ? 'Search a course…' : 'Loading courses…'}
                      helperText="Opens these specific courses for the student and enrols them automatically. No end date — for access that ends on a date, use the Access tab of this student."
                    />
                  )}
                />
              </Box>

              {/* Individually assigned tracks — open every course inside one or
                  more whole tracks for this student, independent of their
                  package / diploma. The API auto-enrols each track's courses so
                  they appear in the student's My Courses right away, and new
                  courses added to the track later open automatically too. */}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Autocomplete
                  multiple
                  id="assignedTracks"
                  options={tracks}
                  value={tracks.filter((t: any) =>
                    (values.assignedTracks ?? []).includes(t?._id)
                  )}
                  getOptionLabel={(option: any) =>
                    option?.title || option?.name || option?.slug || ''
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?._id === value?._id
                  }
                  onChange={(_e, selected: any[]) =>
                    setValue(
                      'assignedTracks',
                      (selected ?? []).map((t: any) => t?._id).filter(Boolean),
                      { shouldDirty: true }
                    )
                  }
                  renderTags={(selected: any[], getTagProps) =>
                    selected.map((option: any, index: number) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option?._id ?? index}
                        size="small"
                        variant="outlined"
                        label={option?.title || option?.name || ''}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign tracks (optional)"
                      placeholder={tracks.length ? 'Search a track…' : 'Loading tracks…'}
                      helperText="Opens every course inside these tracks for the student and enrols them automatically. No end date — for access that ends on a date, use the Access tab of this student."
                    />
                  )}
                />
              </Box>

              {/* Multiple diploma enrolments — a student can join 2+ live diplomas. */}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    Diplomas (a student can join more than one)
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => appendDiploma({ diploma: '', batchName: '' })}
                  >
                    Add diploma
                  </Button>
                </Stack>
                {diplomaFields.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No diploma assigned.
                  </Typography>
                )}
                {paidRemoved.map((x) => (
                  <Alert key={x.id} severity="warning" sx={{ mb: 1.5 }}>
                    {paidText(`bootcamp "${x.title}"`, x.until ?? null)} Removing it here would be refused.
                  </Alert>
                ))}
                <Stack spacing={1.5}>
                  {diplomaFields.map((field, index) => {
                    const rowDiplomaId = values.diplomas?.[index]?.diploma || '';
                    const rowBatch = values.diplomas?.[index]?.batchName || '';
                    const rowBatches = batchesOf(rowDiplomaId);
                    const full = isBatchFull(rowDiplomaId, rowBatch);
                    return (
                      <Box
                        key={field.id}
                        sx={{
                          p: 1.5,
                          border: (t) => `1px solid ${t.palette.divider}`,
                          borderRadius: 1,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <RHFSelect
                            name={`diplomas.${index}.diploma`}
                            label="Diploma"
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            <option value="">Select diploma</option>
                            {diplomas.map((d: any) => (
                              <option key={d._id} value={d._id}>
                                {d.title}
                              </option>
                            ))}
                          </RHFSelect>
                          <RHFSelect
                            name={`diplomas.${index}.batchName`}
                            label="Batch"
                            size="small"
                            sx={{ flex: 1 }}
                            disabled={!rowDiplomaId}
                          >
                            <option value="">Select batch</option>
                            {rowBatches.map((b: any) => (
                              <option key={b._id || b.name} value={b.name}>
                                {b.name}
                                {b.capacity
                                  ? ` (${
                                      batchCounts[`${rowDiplomaId}|${b.name}`] || 0
                                    }/${b.capacity})`
                                  : ''}
                              </option>
                            ))}
                          </RHFSelect>
                          <IconButton color="error" onClick={() => removeDiploma(index)}>
                            <Iconify icon="eva:trash-2-outline" />
                          </IconButton>
                        </Stack>
                        {full && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            This batch is full — assigning will exceed capacity.
                          </Alert>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
              <RHFSelect name="promoCode" label="Promo code (optional)">
                <option value="">None</option>
                {promoCodes.map((p: any) => (
                  <option key={p._id || p.code} value={p.code}>
                    {p.code} — {p.amount}%
                    {p.source === 'partner' && p.partnerName ? ` (${p.partnerName})` : ''}
                  </option>
                ))}
              </RHFSelect>
              {isEdit ? (
                <RHFSwitch name="freeAccess" label="Free access (comped)" />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Free access is given after the account exists: edit the student (reason and end
                  required) or use Access on the student page.
                </Typography>
              )}
              {isEdit && values.freeAccess && (
                <RHFTextField
                  name="freeAccessNote"
                  label={compTurningOn ? 'Reason for free access (required)' : 'Reason for free access'}
                />
              )}
              {compTurningOn && (
                <RHFSwitch name="freeAccessLifetime" label="Lifetime (the free access has no end)" />
              )}
              {isEdit && !compLifetime && (
                <RHFTextField
                  name="accessEndsOn"
                  label={compTurningOn ? 'Free access ends — last day (Cairo time)' : 'Access ends — last day (Cairo time)'}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  inputProps={compTurningOn ? { min: cairoToday() } : undefined}
                  helperText={accessEndsHelper}
                />
              )}
            </Box>

            <Stack alignItems="flex-end" sx={{ pt: 1 }}>
              <LoadingButton
                style={{ width: '200px', height: '50px' }}
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                {!isEdit ? 'Create User' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Stack>
        </FormProvider>
      </Stack>
    </>
  );
}
